import { type EntityType, WorkflowType } from '@communities/content-management/domain';
import { isCommunity } from '@communities/content-management/utils';
import { TabNavigation as CoreTabNavigation } from '@core/components/TabNavigation';
import { FEATURE_FLAGS } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import { QueuesTab } from '@moderation/components/AbrFilterManagement';

import { ClassificationTab } from './ClassificationTab';
import { CommunityContextTab } from './CommunityContextTab';
import { IdentityAccessTab } from './IdentityAccessTab';
import { LandingPageTab } from './LandingPageTab';
import { ModerationTab } from './ModerationTab';
import { StrikeTab } from './StrikeTab';

interface TabNavigationProps {
  entityId: string;
  entityType: EntityType;
  isEntityAdmin: boolean;
  communityGroupId?: string;
  onEditCommunity?: () => void;
}

interface BuildTabsOptions {
  entityId: string;
  entityType: EntityType;
  isEntityAdmin: boolean;
  communityGroupId?: string;
  isAbrFilteringEnabled: boolean;
  onEditCommunity?: () => void;
}

function buildCommunityTabs({ entityId, communityGroupId, isEntityAdmin, isAbrFilteringEnabled, onEditCommunity }: BuildTabsOptions) {
  const tabs = [];

  // Overview tab - visible to ALL users for communities
  tabs.push({
    key: 'overview',
    label: 'Overview',
    content: <LandingPageTab communityGroupId={communityGroupId} communityId={entityId} />,
  });

  // Community Context tab - admin only
  if (isEntityAdmin) {
    tabs.push({
      key: 'community-context',
      label: 'Community Context',
      content: <CommunityContextTab entityId={entityId} isEntityAdmin={isEntityAdmin} onEditCommunity={onEditCommunity} />,
    });
  }

  // Moderation tab - visible to admins only
  if (isEntityAdmin) {
    tabs.push({
      key: 'moderation',
      label: 'Moderation',
      content: <ModerationTab communityId={entityId} isEntityAdmin={isEntityAdmin} />,
    });
  }

  // Queues tab — admin only when ABR filtering feature flag is enabled
  if (isAbrFilteringEnabled && isEntityAdmin) {
    tabs.push({
      key: 'queues',
      label: 'Queues',
      content: <QueuesTab communityId={entityId} />,
    });
  }

  // Strike tab - visible to admins only
  if (isEntityAdmin) {
    tabs.push({
      key: 'strike',
      label: 'Strike',
      content: <StrikeTab communityId={entityId} isEntityAdmin={isEntityAdmin} />,
    });
  }

  return tabs;
}

function buildTabs({ entityId, entityType, isEntityAdmin, communityGroupId, isAbrFilteringEnabled, onEditCommunity }: BuildTabsOptions) {
  const isCommunityEntity = isCommunity(entityType);

  const communityTabs = isCommunityEntity
    ? buildCommunityTabs({
        entityId,
        entityType,
        isEntityAdmin,
        communityGroupId,
        isAbrFilteringEnabled,
        onEditCommunity,
      })
    : [];

  const overview = communityTabs.filter(t => t.key === 'overview');
  const communityContext = communityTabs.filter(t => t.key === 'community-context');
  const moderationAndStrike = communityTabs.filter(t => t.key === 'moderation' || t.key === 'strike');
  const queues = communityTabs.filter(t => t.key === 'queues');

  const adminTabs = isEntityAdmin
    ? [
        {
          key: 'identity-access',
          label: 'Identity & Access',
          content: <IdentityAccessTab entityId={entityId} entityType={entityType} isEntityAdmin={isEntityAdmin} workflowType={WorkflowType.CLASSIFICATION} />,
        },
      ]
    : [];

  const classificationTab = isEntityAdmin
    ? [
        {
          key: 'classification',
          label: 'Classification',
          content: <ClassificationTab entityId={entityId} entityType={entityType} isEntityAdmin={isEntityAdmin} workflowType={WorkflowType.CLASSIFICATION} />,
        },
      ]
    : [];

  // Order: Overview, Identity & Access, Community Context, Classification, Moderation, Queues, Strike
  return [...overview, ...adminTabs, ...communityContext, ...classificationTab, ...moderationAndStrike, ...queues];
}

export function TabNavigation({ entityId, entityType, isEntityAdmin, communityGroupId, onEditCommunity }: TabNavigationProps) {
  const isAbrFilteringEnabled = useIsFeatureEnabled(FEATURE_FLAGS.ABR_FILTERING);

  const isCommunityEntity = isCommunity(entityType);
  const tabs = buildTabs({
    entityId,
    entityType,
    isEntityAdmin,
    communityGroupId,
    isAbrFilteringEnabled,
    onEditCommunity,
  });
  const defaultTab = isCommunityEntity ? 'overview' : 'identity-access';

  return (
    <CoreTabNavigation ariaLabel={`${isCommunityEntity ? 'Community' : 'Community group'} management sections`} defaultActiveTab={defaultTab} tabs={tabs} />
  );
}
